'use client'

import { useEffect, useState } from 'react'
import { useTranslations } from 'next-intl'
import { useForm } from 'react-hook-form'
import { SearchIcon, XIcon, CalendarIcon, FilterIcon } from 'lucide-react'
import { format } from 'date-fns'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle, SheetTrigger } from '@/components/ui/sheet'
import { Label } from '@/components/ui/label'
import { Calendar } from '@/components/ui/calendar'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { ScrollArea } from '@/components/ui/scroll-area'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Combobox } from '@/components/ui/combobox'
import { UsersFilterParams } from '@/types/user'
import { GenderEnum, RoleEnum } from '@/enums'
import { useGroups } from '@/hooks/use-groups'
import { cn } from '@/lib/utils'

type PropsType = {
  filters: UsersFilterParams
  onFiltersChange: (filters: Partial<UsersFilterParams>) => void
  onResetFilters: () => void
}

const Filters = ({ filters, onFiltersChange, onResetFilters }: PropsType) => {
  const t = useTranslations()
  const [isOpen, setIsOpen] = useState(false)

  const { data: groupsData } = useGroups({ size: 999999 })

  const { register, handleSubmit, watch, setValue, reset } = useForm<UsersFilterParams>({
    defaultValues: filters
  })

  useEffect(() => {
    reset(filters)
  }, [filters, reset])

  const watchAll = watch()

  const onSubmit = (data: UsersFilterParams) => {
    onFiltersChange({ ...data, page: 1 })
    setIsOpen(false)
  }

  const handleClearSearch = () => {
    setValue('q', '')
  }

  const formatDateLabel = (value?: string) => (value ? format(new Date(value), 'dd.MM.yyyy') : t('start_date'))

  const handleBlockedChange = (value: string) => {
    if (value === 'all') {
      setValue('blocked', undefined)
    } else {
      setValue('blocked', value === 'true')
    }
  }

  const handleApprovedChange = (value: string) => {
    if (value === 'all') {
      setValue('approved', undefined)
    } else {
      setValue('approved', value === 'true')
    }
  }

  const handleCalendarChange = (field: keyof UsersFilterParams, date?: Date) => {
    if (date) {
      setValue(field, date.toISOString())
    } else {
      setValue(field, '')
    }
  }

  const hasActiveFilters = () => {
    return (
      filters.q ||
      (filters.roles && filters.roles.length > 0) ||
      (filters.genders && filters.genders.length > 0) ||
      (filters.groups && filters.groups.length > 0) ||
      filters.blocked !== undefined ||
      filters.approved !== undefined ||
      filters.createdAtStart ||
      filters.createdAtEnd ||
      filters.updatedAtStart ||
      filters.updatedAtEnd
    )
  }

  return (
    <div className="flex items-center justify-end">
      <Sheet open={isOpen} onOpenChange={setIsOpen}>
        <SheetTrigger asChild>
          <Button variant="outline">
            <FilterIcon />
            <span className="hidden items-center gap-2 lg:flex">{t('filters')}</span>
            {hasActiveFilters() && (
              <span className="bg-primary text-primary-foreground ml-2 flex h-5 w-5 items-center justify-center rounded-full text-xs">
                !
              </span>
            )}
          </Button>
        </SheetTrigger>
        <SheetContent>
          <SheetHeader>
            <SheetTitle>{t('filter_records')}</SheetTitle>
            <SheetDescription>{t('filter_description')}</SheetDescription>
          </SheetHeader>

          <ScrollArea>
            <form onSubmit={handleSubmit(onSubmit)} className="space-y-6 p-4">
              <div className="space-y-2">
                <Label htmlFor="filter-q">{t('keyword')}</Label>
                <div className="relative">
                  <Input
                    placeholder={t('keyword')}
                    id="filter-q"
                    {...register('q')}
                    onKeyDown={e => {
                      if (e.key === 'Enter') {
                        handleSubmit(onSubmit)()
                      }
                    }}
                    icon={<SearchIcon className="h-4 w-4" />}
                    className="pr-8"
                  />
                  {watchAll.q && (
                    <Button
                      variant="ghost"
                      size="sm"
                      className="absolute top-1.5 right-1 h-6 w-6 p-0"
                      onClick={handleClearSearch}
                      type="button">
                      <XIcon className="h-4 w-4" />
                    </Button>
                  )}
                </div>
              </div>

              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <div className="space-y-2">
                  <Label>{t('roles')}</Label>
                  <Combobox
                    options={Object.values(RoleEnum).map(role => ({
                      label: t(role),
                      value: role,
                      searchValue: t(role)
                    }))}
                    value={watchAll.roles || []}
                    onValueChange={value => setValue('roles', value as RoleEnum[])}
                    placeholder={
                      watchAll.roles && watchAll.roles.length > 0
                        ? `${watchAll.roles.length} ${t('selected')}`
                        : t('select')
                    }
                    searchPlaceholder={t('search')}
                    emptyText={t('no_results')}
                    multiple={true}
                    clearable={true}
                  />
                </div>
                <div className="space-y-2">
                  <Label>{t('gender')}</Label>
                  <Combobox
                    options={Object.values(GenderEnum).map(gender => ({
                      label: t(gender),
                      value: gender,
                      searchValue: t(gender)
                    }))}
                    value={watchAll.genders || []}
                    onValueChange={value => setValue('genders', value as GenderEnum[])}
                    placeholder={
                      watchAll.genders && watchAll.genders.length > 0
                        ? `${watchAll.genders.length} ${t('selected')}`
                        : t('select')
                    }
                    searchPlaceholder={t('search')}
                    emptyText={t('no_results')}
                    multiple={true}
                    clearable={true}
                  />
                </div>
                <div className="space-y-2">
                  <Label>{t('blocked')}</Label>
                  <Select
                    value={watchAll.blocked === undefined ? 'all' : watchAll.blocked.toString()}
                    onValueChange={handleBlockedChange}>
                    <SelectTrigger className="w-full">
                      <SelectValue placeholder={t('select')} />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">{t('all')}</SelectItem>
                      <SelectItem value="true">{t('yes')}</SelectItem>
                      <SelectItem value="false">{t('no')}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>{t('approved')}</Label>
                  <Select
                    value={watchAll.approved === undefined ? 'all' : watchAll.approved.toString()}
                    onValueChange={handleApprovedChange}>
                    <SelectTrigger className="w-full">
                      <SelectValue placeholder={t('select')} />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">{t('all')}</SelectItem>
                      <SelectItem value="true">{t('yes')}</SelectItem>
                      <SelectItem value="false">{t('no')}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>{t('groups')}</Label>
                  <Combobox
                    options={
                      groupsData?.items?.map(group => ({
                        label: group.name,
                        value: group.id,
                        searchValue: group.name
                      })) || []
                    }
                    value={watchAll.groups || []}
                    onValueChange={value => setValue('groups', value as string[])}
                    placeholder={
                      watchAll.groups && watchAll.groups.length > 0
                        ? `${watchAll.groups.length} ${t('selected')}`
                        : t('select')
                    }
                    searchPlaceholder={t('search')}
                    emptyText={t('no_results')}
                    multiple={true}
                    clearable={true}
                  />
                </div>
              </div>

              {/** CREATED DATE RANGE */}
              <div className="space-y-2">
                <Label>{t('created_date_range')}</Label>
                <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                  {(['createdAtStart', 'createdAtEnd'] as const).map(field => (
                    <Popover key={field}>
                      <PopoverTrigger asChild>
                        <Button
                          variant="outline"
                          className={cn(
                            'justify-start gap-1 text-left font-normal',
                            !watchAll[field] && 'text-muted-foreground'
                          )}>
                          <CalendarIcon className="mr-1 h-4 w-4" />
                          {watchAll[field]
                            ? formatDateLabel(watchAll[field])
                            : field === 'createdAtStart'
                              ? t('start_date')
                              : t('end_date')}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0">
                        <Calendar
                          captionLayout="dropdown"
                          startMonth={new Date(1900, 0)}
                          endMonth={new Date()}
                          mode="single"
                          selected={watchAll[field] ? new Date(watchAll[field]) : undefined}
                          onSelect={date => handleCalendarChange(field, date || undefined)}
                          autoFocus
                        />
                      </PopoverContent>
                    </Popover>
                  ))}
                </div>
              </div>

              {/** UPDATED DATE RANGE */}
              <div className="space-y-2">
                <Label>{t('updated_date_range')}</Label>
                <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                  {(['updatedAtStart', 'updatedAtEnd'] as const).map(field => (
                    <Popover key={field}>
                      <PopoverTrigger asChild>
                        <Button
                          variant="outline"
                          className={cn(
                            'justify-start gap-1 text-left font-normal',
                            !watchAll[field] && 'text-muted-foreground'
                          )}>
                          <CalendarIcon className="mr-1 h-4 w-4" />
                          {watchAll[field]
                            ? formatDateLabel(watchAll[field])
                            : field === 'updatedAtStart'
                              ? t('start_date')
                              : t('end_date')}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0">
                        <Calendar
                          captionLayout="dropdown"
                          startMonth={new Date(1900, 0)}
                          endMonth={new Date()}
                          mode="single"
                          selected={watchAll[field] ? new Date(watchAll[field]) : undefined}
                          onSelect={date => handleCalendarChange(field, date || undefined)}
                          autoFocus
                        />
                      </PopoverContent>
                    </Popover>
                  ))}
                </div>
              </div>

              <div className="flex flex-row gap-2">
                <Button type="submit" variant="default" size="sm" className="flex-1">
                  <FilterIcon className="mr-2 h-4 w-4" />
                  {t('filter')}
                </Button>
                <Button type="button" onClick={onResetFilters} variant="destructive" size="sm" className="flex-1">
                  <XIcon className="mr-2 h-4 w-4" />
                  {t('clear_filters')}
                </Button>
              </div>
            </form>
          </ScrollArea>
        </SheetContent>
      </Sheet>
    </div>
  )
}

export default Filters
